import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, Mail, Phone, ArrowLeft, Calendar, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { OrderFormData } from '@/types/order';

export default function Confirmation() {
  const navigate = useNavigate();
  const location = useLocation();
  const orderData = location.state?.orderData as OrderFormData | undefined;

  // Rediriger si pas de données
  useEffect(() => {
    if (!orderData) {
      navigate('/commander');
    }
  }, [orderData, navigate]);

  if (!orderData) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-cream via-white to-cream">
      {/* Header */}
      <header className="bg-white border-b border-navy-100">
        <div className="container mx-auto px-6 py-4">
          <div className="flex justify-center">
            <img
              src="/visinot-logo.png"
              alt="VISINOT"
              className="h-10 w-auto"
            />
          </div>
        </div>
      </header>

      {/* Contenu */}
      <div className="container mx-auto px-6 py-12">
        <div className="max-w-3xl mx-auto">
          {/* Success Animation */}
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: 'spring', stiffness: 200, damping: 20, delay: 0.2 }}
            className="flex justify-center mb-8"
          >
            <div className="w-24 h-24 rounded-full bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center shadow-xl">
              <CheckCircle2 className="w-16 h-16 text-white" />
            </div>
          </motion.div>

          {/* Message principal */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-display font-bold text-navy-600 mb-4">
              Commande envoyée avec succès !
            </h2>
            <p className="text-xl text-navy-300 leading-relaxed">
              Merci pour votre confiance. Nous avons bien reçu votre demande pour le bien situé au{' '}
              <span className="font-semibold text-navy-600">
                {orderData.adresseBien}
              </span>
            </p>
          </motion.div>

          {/* Prochaines étapes */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
          >
            <Card>
              <div className="p-8">
                <h3 className="text-2xl font-display font-semibold text-navy-600 mb-6">
                  Prochaines étapes
                </h3>

                <div className="space-y-6">
                  {/* Étape 1 */}
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-gold-100 flex items-center justify-center flex-shrink-0">
                      <Mail className="w-5 h-5 text-gold-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-navy-600 mb-1">
                        Confirmation par email
                      </h4>
                      <p className="text-navy-400 text-sm">
                        Vous allez recevoir un email de confirmation à l'adresse{' '}
                        <span className="font-medium text-navy-600">
                          {orderData.email}
                        </span>{' '}
                        avec le récapitulatif complet de votre commande.
                      </p>
                    </div>
                  </div>

                  {/* Étape 2 */}
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-gold-100 flex items-center justify-center flex-shrink-0">
                      <Phone className="w-5 h-5 text-gold-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-navy-600 mb-1">
                        Contact sous 24h
                      </h4>
                      <p className="text-navy-400 text-sm">
                        Notre équipe vous contactera dans les prochaines 24h pour confirmer tous
                        les détails et répondre à vos questions éventuelles.
                      </p>
                    </div>
                  </div>

                  {/* Étape 3 */}
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-gold-100 flex items-center justify-center flex-shrink-0">
                      <Calendar className="w-5 h-5 text-gold-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-navy-600 mb-1">
                        Mise en place du service
                      </h4>
                      <p className="text-navy-400 text-sm">
                        Nous organiserons ensemble la pose du panneau et le début des visites
                        selon vos disponibilités et les modalités d'accès au bien.
                      </p>
                    </div>
                  </div>

                  {/* Étape 4 */}
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-gold-100 flex items-center justify-center flex-shrink-0">
                      <FileText className="w-5 h-5 text-gold-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-navy-600 mb-1">
                        Rapports réguliers
                      </h4>
                      <p className="text-navy-400 text-sm">
                        Vous recevrez un rapport détaillé après chaque session de visite avec
                        le nombre de visiteurs, leurs commentaires et toute information pertinente.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </motion.div>

          {/* Informations de contact */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="mt-8"
          >
            <Card>
              <div className="p-8 bg-navy-600 text-white rounded-2xl">
                <h3 className="text-xl font-semibold mb-4">
                  Une question ? Contactez-nous
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-gold-400" />
                    <a
                      href="mailto:contact@visinot.be"
                      className="hover:text-gold-400 transition-colors"
                    >
                      contact@visinot.be
                    </a>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone className="w-5 h-5 text-gold-400" />
                    <a
                      href="tel:+32486494670"
                      className="hover:text-gold-400 transition-colors"
                    >
                      0486/49 46 70
                    </a>
                  </div>
                </div>
                <p className="text-sm text-navy-200 mt-4">
                  Nous sommes disponibles du lundi au samedi pour répondre à toutes vos questions.
                </p>
              </div>
            </Card>
          </motion.div>

          {/* Actions */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1 }}
            className="flex flex-col sm:flex-row gap-4 justify-center mt-12"
          >
            <Button
              variant="outline"
              size="lg"
              onClick={() => navigate('/')}
            >
              <ArrowLeft className="w-5 h-5" />
              Retour à l'accueil
            </Button>
            <Button
              variant="gold"
              size="lg"
              onClick={() => navigate('/commander')}
            >
              Nouvelle commande
            </Button>
          </motion.div>

          {/* Numéro de référence fictif */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2 }}
            className="text-center mt-8"
          >
            <p className="text-sm text-navy-300">
              Numéro de référence :{' '}
              <span className="font-mono font-semibold text-navy-600">
                VIS-{new Date().getFullYear()}-{Math.random().toString(36).substr(2, 9).toUpperCase()}
              </span>
            </p>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
